import React, { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Bed, AlertCircle, CheckCircle, Clock } from "lucide-react";
import axios from "axios";
import { useEnquiry } from "./EnquiryContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BedAvailability = () => {
  const { openEnquiryModal } = useEnquiry();
  const [availabilityData, setAvailabilityData] = useState([
    {
      type: "Nursing Care",
      available: 0,
      total: 10,
      color: "blue"
    },
    {
      type: "Dementia Care",
      available: 1,
      total: 8,
      color: "orange"
    },
    {
      type: "End of Life Care",
      available: 2,
      total: 12,
      color: "teal"
    }
  ]);
  const [lastUpdated, setLastUpdated] = useState("Today");

  useEffect(() => {
    fetchBedAvailability();
  }, []);

  const fetchBedAvailability = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bed-availability`);
      if (response.data) {
        setAvailabilityData([
          {
            type: "Nursing Care",
            available: response.data.nursing_available,
            total: response.data.nursing_total,
            color: "blue"
          },
          {
            type: "Dementia Care",
            available: response.data.dementia_available,
            total: response.data.dementia_total,
            color: "orange"
          },
          {
            type: "End of Life Care",
            available: response.data.endoflife_available || response.data.residential_available,
            total: response.data.endoflife_total || response.data.residential_total,
            color: "teal"
          }
        ]);

        // Format last updated date
        const updateDate = new Date(response.data.last_updated);
        const today = new Date();
        const isToday = updateDate.toDateString() === today.toDateString();
        setLastUpdated(isToday ? "Today" : updateDate.toLocaleDateString());
      }
    } catch (error) {
      console.error("Error fetching bed availability:", error);
      // Keep default mock data if API fails
    }
  };

  return (
    <section id="bed-availability" className="py-20 bg-gradient-to-b from-teal-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-orange-500 text-white hover:bg-orange-600">
              <Bed className="h-4 w-4 mr-2" />
              Live Availability
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Current Bed Availability
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Updated today. Contact us to secure your place
            </p>
          </div>

          {/* Availability Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {availabilityData.map((item, index) => (
              <Card
                key={index}
                className={`border-2 ${
                  item.available > 0 ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                } shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <CardContent className="pt-6 text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                    item.available > 0 ? 'bg-green-100' : 'bg-gray-200'
                  }`}>
                    <Bed className={`h-8 w-8 ${
                      item.available > 0 ? 'text-green-600' : 'text-gray-400'
                    }`} />
                  </div>

                  <h3 className="font-bold text-xl text-gray-900 mb-3">{item.type}</h3>

                  <div className="mb-4">
                    <div className={`text-5xl font-bold mb-2 ${
                      item.available > 0 ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {item.available}
                    </div>
                    <div className="text-gray-600">
                      {item.available > 0 ? 'Available Now' : 'Currently Full'}
                    </div>
                  </div>

                  <div className="text-sm text-gray-500 mb-4">
                    {item.available} of {item.total} beds available
                  </div>

                  {item.available > 0 ? (
                    <Badge className="bg-green-600 text-white hover:bg-green-700 w-full justify-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Immediate Admission Possible
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-400 text-white w-full justify-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Enquire Now
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Update Info */}
          <Card className="border-2 border-teal-200 shadow-lg mb-8">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="bg-teal-100 text-teal-700 p-3 rounded-lg flex-shrink-0">
                  <Clock className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">
                    Availability Updated: {lastUpdated}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Bed availability changes frequently. Contact us now to check current availability and arrange an immediate viewing. We respond within 2 hours during business hours.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="border-none shadow-xl bg-gradient-to-r from-teal-600 to-teal-700 text-white">
            <CardContent className="pt-8 pb-8 text-center">
              <h3 className="text-3xl font-bold mb-4">Don't Miss Out. Act Fast</h3>
              <p className="text-teal-50 mb-6 text-lg">
                With limited availability, beds fill quickly. Contact us today to secure a place for your loved one.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => openEnquiryModal("visit")}
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg"
                  data-testid="bed-check-availability-btn"
                >
                  Check Availability Now
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-teal-700 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:01516775573'}
                >
                  Call 0151 677 5573
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
